import type { Role } from "@dishour/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../api/client.js";

export type Me = {
  id: string;
  email: string;
  name: string;
  role: Role;
  restaurantId: string | null;
  supplierId: string | null;
};

export const sessionKey = ["session"] as const;

/**
 * Always hits the network — never reads react-query's cache. `useSession`'s `staleTime: 60_000`
 * means a plain `queryClient.fetchQuery({ queryKey: sessionKey })` can hand back the stale
 * pre-login `null` that was cached on page load, even moments after a successful sign-in.
 * Call this directly (and seed the cache with `queryClient.setQueryData`) whenever the caller
 * needs to know the *current* session right now — e.g. deciding where to redirect after
 * sign-in or invite acceptance.
 */
export async function fetchMe(): Promise<Me | null> {
  const res = await fetch("/api/me", { credentials: "same-origin" });
  if (res.status === 401) return null;
  if (!res.ok) throw new ApiError(res.status, "unknown", "세션을 확인할 수 없습니다.");
  return (await res.json()) as Me;
}

export function useSession() {
  return useQuery<Me | null>({
    queryKey: sessionKey,
    staleTime: 60_000,
    retry: false,
    queryFn: fetchMe,
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // better-auth's sign-out endpoint 415s without a content-type header, and 400s on a
      // truly empty body — an empty JSON object satisfies both.
      await fetch("/api/auth/sign-out", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: "{}",
      });
    },
    onSuccess: () => queryClient.setQueryData(sessionKey, null),
  });
}

export function homePathFor(role: Role): string {
  switch (role) {
    case "restaurant":
      return "/rounds";
    case "supplier":
      return "/supplier";
    case "admin":
      return "/admin/rounds";
  }
}
